<template>
  <div class="bg-gray-100 min-h-screen p-4">
    <div
      ref="printSection"
      class="bg-white shadow rounded-lg max-w-5xl mx-auto p-4"
    >
      <h1 class="text-2xl font-bold text-center mb-4">
        Hospital Visit Summary
      </h1>

      <!-- Patient Biodata -->
      <table class="w-full border mb-3 text-sm">
        <thead class="bg-gray-200">
          <tr>
            <th colspan="4" class="text-left px-2 py-1">Patient Biodata</th>
          </tr>
        </thead>
        <tbody v-if="summary.patient">
          <tr>
            <td class="border px-2 py-1">Name</td>
            <td class="border px-2 py-1">{{ summary.patient.name }}</td>
            <td class="border px-2 py-1">DOB</td>
            <td class="border px-2 py-1">{{ summary.patient.dob }}</td>
          </tr>
          <tr>
            <td class="border px-2 py-1">Gender</td>
            <td class="border px-2 py-1">
              {{ summary.patient.gender == 1 ? "Male" : "Female" }}
            </td>
            <td class="border px-2 py-1">Patient ID</td>
            <td class="border px-2 py-1">{{ summary.patient.id }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Diagnosis -->
      <table
        class="w-full border mb-3 text-sm table-bordered"
        v-if="summary.diagnosis"
      >
        <thead class="bg-gray-200">
          <tr>
            <th colspan="3" class="text-left px-2 py-1">Diagnoses</th>
          </tr>
        </thead>
        <thead class="thead-light">
          <tr>
            <th scope="col">Diagnosis Identity</th>
            <th scope="col">Diagnosis Type</th>
            <th scope="col">Diagnosis Stage</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="diagnosis in summary.diagnosis" :key="diagnosis.id">
            <td>{{ diagnosis.diagnosis }}</td>
            <td>{{ diagnosis.diagnosis_type }}</td>
            <td>{{ diagnosis.diagnosis_stage }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Vitals -->
      <table
        class="w-full border mb-3 text-sm table-bordered"
        v-if="summary.triage"
      >
        <thead class="bg-gray-200">
          <tr>
            <th colspan="6" class="text-left px-2 py-1">Vitals</th>
          </tr>
        </thead>
        <thead class="thead-light">
          <tr>
            <th scope="col">BP</th>
            <th scope="col">Heart Rate</th>
            <th scope="col">Temperature</th>
            <th scope="col">Oxygen Saturation</th>
            <th scope="col">Weight</th>
            <th scope="col">Height</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ summary.triage.blood_pressure }}</td>
            <td>{{ summary.triage.heart_rate }}</td>
            <td>{{ summary.triage.temperature }}</td>
            <td>{{ summary.triage.oxygen_saturation }}</td>
            <td>{{ summary.triage.weight }}</td>
            <td>{{ summary.triage.height }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Labs -->
      <table
        class="w-full border mb-3 text-sm"
        v-if="summary.lab && summary.lab.length"
      >
        <thead class="bg-gray-200">
          <tr>
            <th class="px-2 py-1 text-left">Test</th>
            <th class="px-2 py-1 text-left">Result</th>
            <th class="px-2 py-1 text-left">Note</th>
            <th class="px-2 py-1 text-left">Report</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(lab, index) in summary.lab" :key="index">
            <td class="border px-2 py-1">{{ lab.name }}</td>
            <td class="border px-2 py-1">{{ lab.result }}</td>
            <td class="border px-2 py-1">{{ lab.note }}</td>
            <td class="border px-2 py-1">
              <a
                onmouseover="this.style.color='dodgerblue'"
                onmouseout="this.style.color='skyblue'"
                style="color: skyblue"
                :href="'/lab-details/' + lab.id"
                >View Report</a
              >
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Radiology -->
      <table
        class="w-full border mb-3 text-sm"
        v-if="summary.radiology && summary.radiology.length"
      >
        <thead class="bg-gray-200">
          <tr>
            <th class="px-2 py-1 text-left">Radiology</th>
            <th class="px-2 py-1 text-left">Findings</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rad, index) in summary.radiology" :key="index">
            <td class="border px-2 py-1">{{ rad.name }}</td>
            <td class="border px-2 py-1">{{ rad.note }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Prescriptions -->
      <table
        class="w-full border mb-3 text-sm"
        v-if="summary.prescriptions && summary.prescriptions.length"
      >
        <thead class="bg-gray-200">
          <tr>
            <th class="px-2 py-1 text-left">Medicine</th>
            <th class="px-2 py-1 text-left">Dosage</th>
            <th class="px-2 py-1 text-left">Instructions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(rx, index) in summary.prescriptions" :key="index">
            <td class="border px-2 py-1">
              {{ rx.item_name }} ({{ rx.item_strength }})
            </td>
            <td class="border px-2 py-1">{{ rx.dosage }}</td>
            <td class="border px-2 py-1">{{ rx.instructions }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Procedures -->
      <table
        class="w-full border mb-3 text-sm"
        v-if="summary.procedures && summary.procedures.length"
      >
        <thead class="bg-gray-200">
          <tr>
            <th colspan="2" class="px-2 py-1 text-left">Procedures</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(proc, index) in summary.procedures" :key="index">
            <td class="border px-2 py-1">{{ proc.name }}</td>
            <td class="border px-2 py-1">{{ proc.note }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Doctor Notes -->
      <div
        class="border p-2 text-sm mb-3"
        v-if="summary.doc_notes && summary.doc_notes.length"
      >
        <strong>Doctor Notes:</strong>
        <div v-for="(note, index) in summary.doc_notes" :key="index">
          {{ note.note }}
        </div>
      </div>
    </div>

    <!-- Print Button -->
    <div class="text-center mt-4">
      <button
        @click="printDiv"
        class="bg-blue-600 px-4 py-2 rounded shadow hover:bg-blue-700"
      >
        Print Summary
      </button>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "VisitSummaryPrint",
  data() {
    return {
      summary: {},
    };
  },
  methods: {
    async getSummary() {
      const res = await generalApiRequest(
        "findVisitSummary/" + this.$route.params.visitid,
        "GET",
        {},
        1
      );
      this.summary = res.data;
      console.log(res.data);
    },
    printDiv() {
      const printContents = this.$refs.printSection.innerHTML;
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    },
  },
  mounted() {
    this.getSummary();
  },
};
</script>

<style scoped>
body {
  font-family: "Arial", sans-serif;
  color: black !important;
}
</style>
